import React, { useState, useEffect } from "react";
import {
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, AlertCircle } from "lucide-react";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";

interface StripePaymentFormProps {
  amount: number;
  campaignId?: number;
  planId?: number;
  purchaseType?: string;
  onPaymentSuccess: (paymentIntentId: string) => Promise<void> | void;
  onPaymentError: (error: string) => void;
  isProcessing?: boolean;
  showSavePaymentMethod?: boolean;
}

export const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  amount,
  campaignId,
  planId,
  purchaseType,
  onPaymentSuccess,
  onPaymentError,
  isProcessing = false,
  showSavePaymentMethod = true,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    cardholderName: "",
    email: "",
    streetAddress: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    couponCode: "",
  });

  const [couponApplied, setCouponApplied] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Fetch user email from /me API on component mount
  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const response = await axiosInstance.get(endpoints.me);
        const userData = response.data;
        if (userData.email) {
          setFormData((prev) => ({
            ...prev,
            email: userData.email,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch user email:", error);
        // Continue without pre-filling if API fails
      }
    };

    fetchUserEmail();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyCoupon = async () => {
    if (!formData.couponCode.trim()) {
      toast({
        title: "Invalid Coupon",
        description: "Please enter a coupon code.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Call your backend to validate coupon using axiosInstance
      const { data } = await axiosInstance.post(
        `/campaign/validate-coupon/`,
        {
          code: formData.couponCode,
          amount: amount,
        }
      );

      setDiscountAmount(data.discount || 0);
      setCouponApplied(true);
      toast({
        title: "Coupon Applied",
        description: `Discount of $${data.discount?.toFixed(2)} applied!`,
        variant: "default",
      });
    } catch (error: any) {
      console.error("Coupon validation error:", error);
      toast({
        title: "Invalid Coupon",
        description: error.response?.data?.detail || "The coupon code is invalid or expired.",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      onPaymentError("Stripe not initialized");
      return;
    }

    if (!termsAccepted) {
      toast({
        title: "Terms Required",
        description: "Please accept the terms and conditions.",
        variant: "destructive",
      });
      return;
    }

    // Validate form data
    if (!formData.cardholderName || !formData.email || !formData.streetAddress) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      // Create payment intent on backend using axiosInstance
      const finalAmount = (amount - discountAmount) * 100; // Convert to cents
      if (purchaseType === 'one_time_plan' && !planId) {
        throw new Error('Missing plan ID for one-time plan payment');
      }

      const paymentData = await axiosInstance.post(
        `/campaign/create-payment-intent/`,
        {
          amount: finalAmount,
          campaign_id: campaignId,
          plan_id: planId,
          purchase_type: purchaseType,
          email: formData.email,
          coupon_code: formData.couponCode,
          email_opt_in: emailOptIn,
        }
      ).then(res => res.data);

      // Confirm payment with CardElement
      const { paymentIntent, error: confirmError } =
        await stripe.confirmCardPayment(paymentData.client_secret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: formData.cardholderName,
              email: formData.email,
              address: {
                line1: formData.streetAddress || undefined,
                city: formData.city || undefined,
                state: formData.state || undefined,
                postal_code: formData.zip || undefined,
                country: formData.country || "US",
              },
            },
          },
        });

      if (confirmError) {
        onPaymentError(confirmError.message || "Payment confirmation failed");
        toast({
          title: "Payment Error",
          description: confirmError.message,
          variant: "destructive",
        });
        setProcessing(false);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        // ✅ NEW: Save payment details to database
        try {
          await axiosInstance.post(`/campaign/save-payment/`, {
            payment_intent_id: paymentIntent.id,
            amount: amount - discountAmount,
            currency: 'usd',
            cardholder_name: formData.cardholderName,
            email: formData.email,
            address: formData.streetAddress,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            country: formData.country,
            coupon_code: formData.couponCode || null,
            discount_amount: discountAmount,
            status: 'completed',
          });
          console.log("Payment saved to database successfully");
        } catch (dbError) {
          console.error("Failed to save payment to database:", dbError);
          // Still call success since Stripe payment succeeded
        }

        // ✅ Save payment method if requested
        if (savePaymentMethod) {
          try {
            await axiosInstance.post(`/campaign/save-payment-method/`, {
              payment_method_id: paymentIntent.payment_method,
              set_as_default: false, // User can set default later
              billing_name: formData.cardholderName,
              billing_email: formData.email,
              billing_address_line1: formData.streetAddress,
              billing_address_city: formData.city,
              billing_address_state: formData.state,
              billing_address_zip: formData.zip,
              billing_address_country: formData.country,
            });
            console.log("Payment method saved for future use");
          } catch (saveError) {
            console.error("Failed to save payment method:", saveError);
            // Don't fail the payment if saving payment method fails
          }
        }

        await onPaymentSuccess(paymentIntent.id);
        toast({
          title: "✓ Payment Successful",
          description: savePaymentMethod ? "Your payment has been processed and payment method saved!" : "Your payment has been processed successfully!",
          variant: "default",
          duration: 2000
        });
      } else {
        onPaymentError("Payment intent not succeeded");
      }
    } catch (error: any) {
      console.error("Payment error:", error.response?.data || error);
      const message =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        "An error occurred during payment";
      onPaymentError(message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Coupon Code Section */}
      {/* <div className="border-b pb-6">
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Enter Coupon Code:
        </label>
        <div className="flex gap-2">
          <Input
            type="text"
            name="couponCode"
            placeholder="Coupon code"
            value={formData.couponCode}
            onChange={handleInputChange}
            disabled={couponApplied}
            className="flex-1"
          />
          <Button
            type="button"
            onClick={applyCoupon}
            disabled={couponApplied}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Add
          </Button>
          {couponApplied && (
            <a href="#" className="text-blue-600 text-sm self-center">
              Whats This?
            </a>
          )}
        </div>
      </div> */}

      {/* Payment Details Section */}
      <div className="space-y-6 border-b pb-6">
        <h3 className="text-sm font-semibold text-gray-900">Payment Details</h3>
        <p className="text-sm text-gray-600">
          To complete your order enter your payment details in the fields below.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Cardholder Name */}
            <div>
              <Label
                htmlFor="cardholderName"
                className="text-sm font-semibold text-gray-900 mb-2"
              >
                Card Holders Name:
              </Label>
              <Input
                id="cardholderName"
                name="cardholderName"
                placeholder="Card Holders Name"
                value={formData.cardholderName}
                onChange={handleInputChange}
                className="border-green-500"
                required
              />
            </div>

            {/* Email */}
            <div>
              <Label
                htmlFor="email"
                className="text-sm font-semibold text-gray-900 mb-2"
              >
                Email Address:
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleInputChange}
                className="border-green-500"
                required
                disabled={!!formData.email} // Disable if email is pre-filled from /me API
              />
            </div>

            {/* Card Details */}
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-2">
                Card Details:
              </Label>
              <div className="border border-green-500 rounded-md p-3 bg-white">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: "14px",
                        color: "#000",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        "::placeholder": {
                          color: "#999",
                        },
                      },
                      invalid: {
                        color: "#fa755a",
                      },
                    },
                  }}
                />
              </div>
            </div>

              {/* Country */}
            <div>
              <Label
                htmlFor="country"
                className="text-sm font-semibold text-gray-900 mb-2"
              >
                Country:
              </Label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full border border-green-500 rounded-md px-3 py-2 bg-white text-gray-700"
                required
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="NZ">New Zealand</option>
                <option value="MX">Mexico</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
                <option value="IT">Italy</option>
                <option value="ES">Spain</option>
                <option value="JP">Japan</option>
                <option value="CN">China</option>
                <option value="IN">India</option>
                <option value="BR">Brazil</option>
              </select>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            {/* Street Address */}
            <div>
              <Label
                htmlFor="streetAddress"
                className="text-sm font-semibold text-gray-900 mb-2"
              >
                Street Address:
              </Label>
              <Input
                id="streetAddress"
                name="streetAddress"
                placeholder="e.g. Street Address"
                value={formData.streetAddress}
                onChange={handleInputChange}
                className="border-green-500"
                required
              />
            </div>

            {/* City */}
            <div>
              <Label
                htmlFor="city"
                className="text-sm font-semibold text-gray-900 mb-2"
              >
                City:
              </Label>
              <Input
                id="city"
                name="city"
                placeholder="e.g. Cincinnati"
                value={formData.city}
                onChange={handleInputChange}
                className="border-green-500"
                required
              />
            </div>

            {/* State & Zip */}
            <div className="grid grid gap-4">
              <div>
                <Label
                  htmlFor="state"
                  className="text-sm font-semibold text-gray-900 mb-2"
                >
                  State:
                </Label>
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="w-full border border-green-500 rounded-md px-3 py-2 bg-white text-gray-700"
                  required
                >
                  <option value="">-- Select a State --</option>
                  <option value="AL">Alabama</option>
                  <option value="AK">Alaska</option>
                  <option value="AZ">Arizona</option>
                  <option value="AR">Arkansas</option>
                  <option value="CA">California</option>
                  <option value="CO">Colorado</option>
                  <option value="CT">Connecticut</option>
                  <option value="DE">Delaware</option>
                  <option value="FL">Florida</option>
                  <option value="GA">Georgia</option>
                  <option value="HI">Hawaii</option>
                  <option value="ID">Idaho</option>
                  <option value="IL">Illinois</option>
                  <option value="IN">Indiana</option>
                  <option value="IA">Iowa</option>
                  <option value="KS">Kansas</option>
                  <option value="KY">Kentucky</option>
                  <option value="LA">Louisiana</option>
                  <option value="ME">Maine</option>
                  <option value="MD">Maryland</option>
                  <option value="MA">Massachusetts</option>
                  <option value="MI">Michigan</option>
                  <option value="MN">Minnesota</option>
                  <option value="MS">Mississippi</option>
                  <option value="MO">Missouri</option>
                  <option value="MT">Montana</option>
                  <option value="NE">Nebraska</option>
                  <option value="NV">Nevada</option>
                  <option value="NH">New Hampshire</option>
                  <option value="NJ">New Jersey</option>
                  <option value="NM">New Mexico</option>
                  <option value="NY">New York</option>
                  <option value="NC">North Carolina</option>
                  <option value="ND">North Dakota</option>
                  <option value="OH">Ohio</option>
                  <option value="OK">Oklahoma</option>
                  <option value="OR">Oregon</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="RI">Rhode Island</option>
                  <option value="SC">South Carolina</option>
                  <option value="SD">South Dakota</option>
                  <option value="TN">Tennessee</option>
                  <option value="TX">Texas</option>
                  <option value="UT">Utah</option>
                  <option value="VT">Vermont</option>
                  <option value="VA">Virginia</option>
                  <option value="WA">Washington</option>
                  <option value="WV">West Virginia</option>
                  <option value="WI">Wisconsin</option>
                  <option value="WY">Wyoming</option>
                  <option value="DC">District of Columbia</option>
                  <option value="PR">Puerto Rico</option>
                </select>
              </div>

              {/* <div>
                <Label
                  htmlFor="zip"
                  className="text-sm font-semibold text-gray-900 mb-2"
                >
                  Zip:
                </Label>
                <Input
                  id="zip"
                  name="zip"
                  placeholder="e.g. 12345"
                  value={formData.zip}
                  onChange={handleInputChange}
                  className="border-green-500"
                  maxLength={6}
                  required
                />
              </div> */}
            </div>

          
          </div>
        </div>

        {/* Credit Cards Info */}
        <div className="p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-semibold text-gray-900 mb-3">
            We accept all major credit cards
          </p>
          <div className="flex gap-3">
            <img
              src="https://mylistiq-production.s3.us-east-1.amazonaws.com/static/image/visa.png"
              alt="Visa"
              className="h-8"
            />
            <img
              src="https://mylistiq-production.s3.us-east-1.amazonaws.com/static/image/card.png"
              alt="Mastercard"
              className="h-8"
            />
            <img
              src="https://mylistiq-production.s3.us-east-1.amazonaws.com/static/image/discover.png"
              alt="Discover"
              className="h-8"
            />
            <img
              src="https://mylistiq-production.s3.us-east-1.amazonaws.com/static/image/amex.png"
              alt="Amex"
              className="h-8"
            />
          </div>
        </div>
      </div>

      {/* Order Opt In */}
      {/* <div className="border-b pb-6">
        <label className="block text-sm font-semibold text-gray-900 mb-3">
          Order Opt In
        </label>
        <div className="flex gap-3">
          <div className="flex items-center gap-2">
            <input
              type="radio"
              id="optInYes"
              name="emailOptIn"
              value="yes"
              checked={emailOptIn}
              onChange={() => setEmailOptIn(true)}
              className="w-4 h-4"
            />
            <label htmlFor="optInYes" className="text-sm text-gray-900">
              Yes
            </label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="radio"
              id="optInNo"
              name="emailOptIn"
              value="no"
              checked={!emailOptIn}
              onChange={() => setEmailOptIn(false)}
              className="w-4 h-4"
            />
            <label htmlFor="optInNo" className="text-sm text-gray-900">
              No
            </label>
          </div>
          <span className="text-sm text-gray-600 ml-2">
            Make sure I get a copy at {formData.email || "your email"}
          </span>
        </div>
      </div> */}

      {/* Terms & Conditions */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="termsAccepted"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="w-5 h-5"
            required
          />
          <label
            htmlFor="termsAccepted"
            className="text-sm text-gray-700 leading-tight"
          >
            I have read and agree to the terms and conditions on the previous page
          </label>
        </div>

        {/* {showSavePaymentMethod && (
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="savePaymentMethod"
              checked={savePaymentMethod}
              onChange={(e) => setSavePaymentMethod(e.target.checked)}
              className="w-5 h-5"
            />
            <label
              htmlFor="savePaymentMethod"
              className="text-sm text-gray-700 leading-tight"
            >
              Save this payment method for future purchases
            </label>
          </div>
        )} */}
      </div>


      {/* Price Summary */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-700">Subtotal:</span>
            <span className="font-semibold">${amount.toFixed(2)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-green-600">
              <span className="text-sm">Discount:</span>
              <span className="font-semibold">-${discountAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t border-gray-300 pt-2 flex justify-between">
            <span className="font-semibold text-gray-900">Total Amount:</span>
            <span className="text-lg font-bold text-green-600">
              ${(amount - discountAmount).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={!stripe || processing || isProcessing || !termsAccepted}
        className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg font-semibold"
      >
        {processing || isProcessing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Processing...
          </>
        ) : (
          "Complete Order"
        )}
      </Button>

      {/* Error Display */}
      {!stripe && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">
            Payment processing is not available. Please try again later.
          </p>
        </div>
      )}
    </form>
  );
};

export default StripePaymentForm;
